import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../services/apiClient';

// PM Fuel (Pat, Oct 9 2026): PMs filling their own vehicles from the company tank, logged on the fill-up
// with "⛽ PM fuel — my own vehicle". Only the Executives and the office see this; it's to be billed back,
// so each fill-up is marked billed when it has been. Gallons only. Not in the trucks' fuel log or any feed.
const btn = { padding: '6px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' };
const on = { background: '#b45309', borderColor: '#b45309', color: '#fff' };
const pad = n => String(n).padStart(2, '0');
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const gal = n => `${Math.round((Number(n) || 0) * 10) / 10} gal`;
const day = s => (s ? new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function ranges() {
  const now = new Date();
  const m0 = new Date(now.getFullYear(), now.getMonth(), 1), lm0 = new Date(now.getFullYear(), now.getMonth() - 1, 1), lm1 = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    month: { label: 'This month', from: ymd(m0), to: '' },   // open-ended: a fill-up dated by the phone's clock can be a day ahead
    last: { label: 'Last month', from: ymd(lm0), to: ymd(lm1) },
    year: { label: 'This year', from: `${now.getFullYear()}-01-01`, to: '' },
    all: { label: 'Everything', from: '', to: '' },
  };
}

export default function PmFuelPanel() {
  const R = ranges();
  const [pick, setPick] = useState('month');
  const [who, setWho] = useState('');
  const [show, setShow] = useState('to_bill');   // to_bill | billed | all
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const range = R[pick];
  const load = () => apiFetch(`/pm-fuel?from=${range.from}&to=${range.to}`).then(d => { setData(d); setErr(''); }).catch(e => setErr(e?.error || 'Could not load PM fuel.'));
  useEffect(() => { setData(null); load(); }, [pick]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function setBilled(r, billed) {
    setBusy(r.id);
    try { await apiFetch(`/pm-fuel/${r.id}/billed`, { method: 'POST', body: JSON.stringify({ billed }) }); await load(); }
    catch (e) { setErr(e?.error || 'Could not save that.'); }
    setBusy('');
  }
  const rows = useMemo(() => (data?.rows || []).filter(r => (!who || r.user_id === who) && (show === 'all' || (show === 'billed' ? !!r.billed_at : !r.billed_at))), [data, who, show]);

  return (
    <div>
      <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 10 }}>
        Fuel PMs put in their own vehicles from the company tank — ticked “PM fuel” on the fill-up. Only the Executives and the office see this.
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {Object.entries(R).map(([k, r]) => <button key={k} onClick={() => setPick(k)} style={{ ...btn, ...(pick === k ? on : null) }}>{r.label}</button>)}
        <span style={{ width: 10 }} />
        {[['to_bill', 'To be billed'], ['billed', 'Billed'], ['all', 'All']].map(([k, l]) => <button key={k} onClick={() => setShow(k)} style={{ ...btn, padding: '4px 10px', fontSize: 12, ...(show === k ? { background: '#1e3a5f', borderColor: '#1e3a5f', color: '#fff' } : null) }}>{l}</button>)}
      </div>
      {err && <div style={{ color: '#b91c1c', fontWeight: 700, marginBottom: 10 }}>{err}</div>}
      {!data && !err && <div style={{ color: '#9ca3af', padding: 16 }}>Loading…</div>}
      {data && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            {data.pms.length === 0 && <div style={{ color: '#9ca3af' }}>No PM fuel logged {pick === 'all' ? 'yet' : 'in that time'}.</div>}
            {data.pms.map(p => (
              <button key={p.user_id} onClick={() => setWho(w => (w === p.user_id ? '' : p.user_id))}
                style={{ textAlign: 'left', background: who === p.user_id ? '#fff7ed' : '#fff', border: `1.5px solid ${who === p.user_id ? '#b45309' : '#e5e7eb'}`, borderRadius: 12, padding: '10px 14px', minWidth: 170, cursor: 'pointer', fontFamily: 'inherit' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{p.name}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: p.to_bill > 0 ? '#b45309' : '#15803d', marginTop: 2 }}>{gal(p.to_bill)}</div>
                <div style={{ fontSize: 11.5, color: '#6b7280' }}>to be billed · {gal(p.gallons)} in all · {p.fills} fill-up{p.fills === 1 ? '' : 's'}</div>
              </button>
            ))}
          </div>
          {data.pms.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              {rows.length === 0 ? <div style={{ padding: 16, color: '#9ca3af' }}>Nothing {show === 'billed' ? 'billed' : show === 'to_bill' ? 'waiting to be billed' : ''} here.</div> : rows.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderTop: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: '#6b7280', minWidth: 96 }}>{day(r.filled_at)}</span>
                  <span style={{ fontSize: 14, fontWeight: 800, minWidth: 150 }}>{r.name}</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: '#b45309', minWidth: 70 }}>{gal(r.gallons)}</span>
                  <span style={{ flex: 1, fontSize: 13, color: '#374151', minWidth: 120 }}>{r.note || ''}</span>
                  {r.billed_at
                    ? <span style={{ fontSize: 12, color: '#15803d', fontWeight: 700 }}>✓ billed {day(String(r.billed_at).slice(0, 10))}{r.billed_by_name ? ` · ${r.billed_by_name}` : ''} <button onClick={() => setBilled(r, false)} disabled={busy === r.id} style={{ ...btn, padding: '2px 8px', fontSize: 11, marginLeft: 6 }}>Undo</button></span>
                    : <button onClick={() => setBilled(r, true)} disabled={busy === r.id} style={{ ...btn, background: '#15803d', borderColor: '#15803d', color: '#fff' }}>{busy === r.id ? '…' : 'Mark billed'}</button>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
